import { useState } from 'react';
import { Modal } from 'reactstrap';
import { IconClipboardCheck } from '@tabler/icons-react';
import { RentVisitFeedbackBody } from '../services/dashboardService';
import {
  ALL_GOOD,
  INTEREST_LEVEL_OPTIONS,
  MAX_RECOMMENDATION,
  MOVE_IN_OPTIONS,
  PROPERTY_CONDITION_OPTIONS,
  RENT_OBJECTION_OPTIONS,
  SALE_OBJECTION_OPTIONS,
} from '../constants/rentFeedbackOptions';
import { Chips } from './PreSaleVisitFeedbackModal';

export type VisitFeedbackKind = 'rent' | 'sale';

interface Props {
  isOpen: boolean;
  kind: VisitFeedbackKind;
  propertyName: string;
  error?: string;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (feedback: RentVisitFeedbackBody) => void;
}

// "¿Cómo te fue en la visita?" para Renta (inquilino) y Venta (comprador).
// Se envía antes de marcar la visita como realizada.
const VisitFeedbackModal = ({
  isOpen,
  kind,
  propertyName,
  error,
  loading,
  onClose,
  onSubmit,
}: Props) => {
  const isSale = kind === 'sale';
  const [interest, setInterest] = useState('');
  const [objections, setObjections] = useState<string[]>([]);
  const [conditions, setConditions] = useState<string[]>([]);
  const [moveIn, setMoveIn] = useState('');
  const [recommendation, setRecommendation] = useState('');
  const [hadIssue, setHadIssue] = useState<boolean | null>(null);
  const [issueDetails, setIssueDetails] = useState('');
  const [tried, setTried] = useState(false);

  const valid =
    !!interest &&
    conditions.length > 0 &&
    !!recommendation.trim() &&
    (!isSale || hadIssue !== null);

  const toggle = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    value: string,
  ) =>
    setter((prev) =>
      prev.includes(value) ? prev.filter((x) => x !== value) : [...prev, value],
    );

  // "Todo en orden" no se combina con otras opciones.
  const toggleCondition = (value: string) =>
    setConditions((prev) => {
      if (value === ALL_GOOD) return prev.includes(ALL_GOOD) ? [] : [ALL_GOOD];
      const without = prev.filter((x) => x !== ALL_GOOD);
      return without.includes(value)
        ? without.filter((x) => x !== value)
        : [...without, value];
    });

  const submit = () => {
    setTried(true);
    if (!valid) return;
    const body: RentVisitFeedbackBody = {
      interest_level: interest,
      objections,
      property_condition: conditions,
      owner_recommendation: recommendation.trim(),
    };
    if (isSale) {
      body.logistics_issue = {
        had_issue: !!hadIssue,
        ...(hadIssue && issueDetails.trim()
          ? { details: issueDetails.trim() }
          : {}),
      };
    } else if (moveIn) {
      body.move_in_timeframe = moveIn;
    }
    onSubmit(body);
  };

  const err = (missing: boolean) =>
    tried && missing ? (
      <small className="vfm-error">Este campo es obligatorio</small>
    ) : null;

  const person = isSale ? 'comprador' : 'inquilino';

  return (
    <Modal
      isOpen={isOpen}
      toggle={loading ? undefined : onClose}
      centered
      size="lg"
      className="vfm"
    >
      <div className="vfm-body">
        <div className="vfm-head">
          <span className="vfm-icon">
            <IconClipboardCheck size={24} />
          </span>
          <div>
            <h2 className="vfm-title">¿Cómo te fue en la visita?</h2>
            <p className="vfm-sub">
              Llena esto para cerrar la visita a {propertyName}.
            </p>
          </div>
        </div>

        <div className="vfm-field">
          <label>
            ¿Qué tan interesado viste al {person}? <b>*</b>
          </label>
          <Chips
            options={INTEREST_LEVEL_OPTIONS}
            value={interest ? [interest] : []}
            onToggle={setInterest}
          />
          {err(!interest)}
        </div>

        <div className="vfm-field">
          <label>
            ¿Qué objeciones escuchaste durante la visita? <span>(opcional)</span>
          </label>
          <Chips
            options={isSale ? SALE_OBJECTION_OPTIONS : RENT_OBJECTION_OPTIONS}
            value={objections}
            onToggle={(v) => toggle(setObjections, v)}
          />
        </div>

        <div className="vfm-field">
          <label>
            ¿Cómo encontraste la propiedad hoy? <b>*</b>
          </label>
          <div className="vfm-checks">
            {PROPERTY_CONDITION_OPTIONS.map((option) => (
              <label key={option.value} className="vfm-check">
                <input
                  type="checkbox"
                  checked={conditions.includes(option.value)}
                  onChange={() => toggleCondition(option.value)}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
          {err(conditions.length === 0)}
        </div>

        {!isSale && (
          <div className="vfm-field">
            <label>
              ¿Cuándo necesita mudarse el inquilino? <span>(opcional)</span>
            </label>
            <Chips
              options={MOVE_IN_OPTIONS}
              value={moveIn ? [moveIn] : []}
              onToggle={(v) => setMoveIn(moveIn === v ? '' : v)}
            />
          </div>
        )}

        <div className="vfm-field">
          <label>
            {isSale
              ? '¿Qué le recomendarías al propietario para mejorar sus posibilidades de venta?'
              : '¿Qué le recomendarías al propietario?'}{' '}
            <b>*</b>
          </label>
          <textarea
            className="vfm-input"
            rows={3}
            maxLength={MAX_RECOMMENDATION}
            value={recommendation}
            onChange={(e) => setRecommendation(e.target.value)}
            placeholder="Escribe una recomendación breve..."
          />
          {err(!recommendation.trim())}
        </div>

        {isSale && (
          <div className="vfm-field">
            <label>
              ¿Hubo algún problema logístico con la visita? <b>*</b>
            </label>
            <Chips
              options={[
                { value: 'yes', label: 'Sí' },
                { value: 'no', label: 'No' },
              ]}
              value={hadIssue === null ? [] : [hadIssue ? 'yes' : 'no']}
              onToggle={(v) => setHadIssue(v === 'yes')}
            />
            {hadIssue && (
              <input
                className="vfm-input mt-2"
                value={issueDetails}
                onChange={(e) => setIssueDetails(e.target.value)}
                placeholder="Cuéntanos qué pasó (opcional)..."
              />
            )}
            {err(hadIssue === null)}
          </div>
        )}

        {error && <div className="vfm-error vfm-error--box">{error}</div>}

        <div className="vfm-footer">
          <button
            type="button"
            className="vfm-btn vfm-btn--ghost"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="vfm-btn vfm-btn--primary"
            onClick={submit}
            disabled={loading}
          >
            {loading ? 'Guardando…' : 'Finalizar visita'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default VisitFeedbackModal;
